import React from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField } from '@/shared/ui';
import {
  APPOINTMENT_DURATION_OPTIONS,
  MIN_APPOINTMENT_DURATION,
  parseAppointmentDurationInput,
} from '../utils/appointmentTypes';

interface AppointmentDurationFieldProps {
  value: number | null;
  onChange: (minutes: number | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  hasTreatment: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  name?: string;
  id?: string;
}

const DURATION_VALUES = APPOINTMENT_DURATION_OPTIONS.map((option) => option.value);

const AppointmentDurationField: React.FC<AppointmentDurationFieldProps> = ({
  value,
  onChange,
  onBlur,
  hasTreatment,
  disabled = false,
  error = false,
  helperText,
  required = true,
  name = 'duration',
  id = 'duration',
}) => {
  const { t } = useTranslation(['appointment']);

  if (hasTreatment) {
    return (
      <TextField
        fullWidth
        required={required}
        type="number"
        id={id}
        name={name}
        label={t('fields.durationInMinutes')}
        placeholder={t('fields.durationPlaceholder')}
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw.trim() === '') {
            onChange(null);
            return;
          }
          onChange(parseAppointmentDurationInput(raw, null));
        }}
        onBlur={onBlur}
        error={error}
        helperText={helperText}
        disabled={disabled}
        inputProps={{ min: MIN_APPOINTMENT_DURATION, step: 1 }}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo
      options={DURATION_VALUES}
      getOptionLabel={(option) => String(option)}
      renderOption={(props, option) => (
        <li {...props} key={option}>
          {t('fields.durationMinutes', { count: option })}
        </li>
      )}
      value={value}
      onChange={(_event, next) => {
        if (next == null || next === '') {
          onChange(null);
          return;
        }
        onChange(parseAppointmentDurationInput(next, null));
      }}
      onInputChange={(_event, inputValue, reason) => {
        if (reason === 'clear') {
          onChange(null);
          return;
        }
        if (reason !== 'input') return;
        if (inputValue.trim() === '') {
          onChange(null);
          return;
        }
        onChange(parseAppointmentDurationInput(inputValue, null));
      }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          id={id}
          name={name}
          label={t('fields.durationInMinutes')}
          placeholder={t('fields.durationPlaceholder')}
          type="number"
          onBlur={onBlur}
          error={error}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            min: MIN_APPOINTMENT_DURATION,
            step: 1,
          }}
        />
      )}
    />
  );
};

export default AppointmentDurationField;
